import moment from 'moment-timezone';
import {
  computeATR,
  computeEMA,
  computeSwings,
  computeTrendState,
  computeVWAP,
  detectRange,
  computeSessionBounds
} from './indicatorUtils.ts';
import type {
  Candle,
  DetectedSetup,
  IndicatorSeries,
  LiquidityZone,
  RangeState,
  SessionKey,
  SetupEngineParams,
  SetupEngineOutput,
  SwingPoint,
  TrendState
} from './types.ts';
import {
  detectEmaPullback,
  detectFailedBreakout,
  detectFlag,
  detectMomentumReversal,
  detectOrb,
  detectRangeFade,
  detectVwapReclaim
} from './setupDetectors.ts';
import { applyExecutionModels, detectFVGs } from './fvgExecution.ts';

const TIME_ZONE = 'America/New_York';

const getTimeframeMinutes = (timeframe: SetupEngineParams['timeframe']) => {
  switch (timeframe) {
    case '1m':
      return 1;
    case '5m':
      return 5;
    case '15m':
      return 15;
    case '1h':
    default:
      return 60;
  }
};

const resolveSessionStartIndex = (
  candles: Candle[],
  session: SessionKey,
  sessionStart?: string,
  sessionEnd?: string
) => {
  if (candles.length === 0) return null;
  for (let i = candles.length - 1; i >= 0; i -= 1) {
    const bounds = computeSessionBounds(candles[i].t, session, sessionStart, sessionEnd);
    const time = moment.tz(candles[i].t, TIME_ZONE);
    if (time.isBetween(bounds.start, bounds.end, undefined, '[]')) {
      // Find the first candle inside this session window.
      let firstIndex = i;
      for (let j = i; j >= 0; j -= 1) {
        const inSession = moment
          .tz(candles[j].t, TIME_ZONE)
          .isBetween(bounds.start, bounds.end, undefined, '[]');
        if (!inSession) {
          break;
        }
        firstIndex = j;
      }
      return firstIndex;
    }
  }
  return null;
};

export const buildIndicators = (
  candles: Candle[],
  session: SessionKey,
  sessionStart?: string,
  sessionEnd?: string
): IndicatorSeries => {
  const closes = candles.map((c) => c.c);
  const ema9 = computeEMA(closes, 9);
  const ema20 = computeEMA(closes, 20);
  const atr14 = computeATR(candles, 14);
  const { vwap, label } = computeVWAP(candles, session, sessionStart, sessionEnd);

  return {
    ema9,
    ema20,
    atr14,
    vwap,
    vwapLabel: label
  };
};

export const runSetupEngine = (params: SetupEngineParams): SetupEngineOutput => {
  const { candles, session, sessionStart, sessionEnd, strictMode, onlyActive, symbol, timeframe, liquidityZones = [] } = params;
  const indicators = buildIndicators(candles, session, sessionStart, sessionEnd);
  const swings = computeSwings(candles, 2, 2);
  const trend = computeTrendState(swings) as TrendState;
  const range = detectRange(candles, indicators.atr14, 20, 1.5) as RangeState | null;
  const hasVolume = candles.some((c) => typeof c.v === 'number' && c.v > 0);
  const sessionStartIndex = resolveSessionStartIndex(candles, session, sessionStart, sessionEnd);
  const orbWindowBars = Math.max(1, Math.round(30 / getTimeframeMinutes(timeframe)));

  const setups = [
    ...detectEmaPullback({ symbol, timeframe, candles, indicators, swings, trend, strictMode, hasVolume }),
    ...detectFlag({ symbol, timeframe, candles, indicators, strictMode, hasVolume }),
    ...detectVwapReclaim({ symbol, timeframe, candles, indicators, session, strictMode, hasVolume }),
    ...detectRangeFade({ symbol, timeframe, candles, range, strictMode, hasVolume }),
    ...detectFailedBreakout({ symbol, timeframe, candles, range, strictMode, hasVolume }),
    ...detectOrb({ symbol, timeframe, candles, sessionStartIndex, orbWindowBars, strictMode, hasVolume }),
    ...detectMomentumReversal({ symbol, timeframe, candles, indicators, strictMode, hasVolume })
  ];

  const fvgCandidates = detectFVGs(candles, { timeframe });

  const enrichedSetups = setups.map((setup) =>
    applyExecutionModels({
      setup,
      candles,
      indicators,
      trend,
      range,
      session,
      sessionStart,
      sessionEnd,
      fvgCandidates,
      swings
    })
  );

  const parseZoneWindow = (zone: LiquidityZone) => ({
    start: moment.tz(zone.startTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf(),
    end: moment.tz(zone.endTime, 'YYYY-MM-DD HH:mm:ss', 'UTC').valueOf()
  });

  const zoneMatchesSetup = (zone: LiquidityZone, setup: DetectedSetup) => {
    const window = parseZoneWindow(zone);
    const detectedAt = setup.detectedAt;
    if (detectedAt < window.start || detectedAt > window.end) {
      return false;
    }
    const type = (zone.type ?? '').toLowerCase();
    if (type === 'demand' && setup.direction !== 'LONG') return false;
    if (type === 'supply' && setup.direction !== 'SHORT') return false;
    return true;
  };

  const findLiquidityZoneForSetup = (setup: DetectedSetup): LiquidityZone | undefined => {
    const candidates = liquidityZones
      .filter((zone) => zoneMatchesSetup(zone, setup))
      .map((zone) => ({
        zone,
        distance: Math.abs(setup.detectedAt - (parseZoneWindow(zone).start + parseZoneWindow(zone).end) / 2)
      }))
      .sort((a, b) => a.distance - b.distance);
    return candidates[0]?.zone;
  };

  const setupsWithZones = enrichedSetups.map((setup) => ({
    ...setup,
    liquidityZone: findLiquidityZoneForSetup(setup)
  }));

  const filtered = onlyActive
    ? setupsWithZones.filter((setup) => setup.status !== 'EXPIRED')
    : setupsWithZones;

  return {
    indicators,
    swings,
    trend,
    range,
    setups: filtered
  };
};
